/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Link } from 'react-router';

import { IconButton, NotificationBadge } from '@coursera/cds-core';
import { CartIcon } from '@coursera/cds-icons';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { useShoppingCart } from 'bundles/composable-page-header-consumer/contexts/ShoppingCartContext';
import IconButtonWrapper from 'bundles/composable-page-header/components/IconButtonWrapper';
import {
  DEFAULT_ICON_BUTTON_HEIGHT,
  DEFAULT_ICON_BUTTON_SVG_HEIGHT,
  TRACKING_NAMES,
} from 'bundles/composable-page-header/constants';
import { cartUrl, rootPath } from 'bundles/payments-common/common/constants';
import { get as savedCartUtilsGet, reset as savedCartUtilsReset } from 'bundles/payments-common/lib/savedCartUtils';

import _t from 'i18n!nls/composable-page-header-consumer';

const styles = {
  badge: css`
    > .cds-notificationBadge-top {
      position: relative;
      left: -5px;
      top: 2px;
    }
  `,
};

const getCartPageUrl = () => {
  const cartInfo = savedCartUtilsGet();
  const cartId = cartInfo?.id;

  if (cartId) {
    return `${rootPath}/${cartUrl}?${new URLSearchParams([['cartId', cartId.toString()]])}`;
  } else {
    return '/';
  }
};

const ShoppingCartGridItem = () => {
  const track = useTracker();
  const shoppingCart = useShoppingCart();
  const cartPageUrl = React.useMemo(() => getCartPageUrl(), []);
  const ariaText = _t('View your shopping cart');

  const trackingData = {
    button: {
      name: TRACKING_NAMES.SHOPPING_CART,
      linkType: 'internal' as const,
      linkURL: cartPageUrl,
    },
    ...(shoppingCart?.cart?.id &&
      shoppingCart?.cart?.cart && {
        paymentCart: {
          id: String(shoppingCart?.cart.id),
          creationDate: new Date(shoppingCart?.cart.cart.createdAt),
          amount: shoppingCart?.cart?.totalAmount,
          currencyId: shoppingCart?.cart?.currencyId?.toString(),
        },
      }),
  };

  const visibilityTrackingRef: React.MutableRefObject<HTMLSpanElement | null> = useVisibilityTracker(
    'view_button',
    trackingData
  );

  React.useEffect(() => {
    if (!shoppingCart?.loading && !shoppingCart?.isValidCart) {
      savedCartUtilsReset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!shoppingCart?.isValidCart) {
    return null;
  }

  return (
    <NotificationBadge align="top" value={1} css={styles.badge}>
      <span ref={visibilityTrackingRef}>
        {/* The value should always be 1 when there is a cart, as it represents "1 cart" */}
        <IconButtonWrapper
          iconButton={
            <IconButton
              tooltip={ariaText}
              aria-label={ariaText}
              href={cartPageUrl}
              size="small"
              variant="ghost"
              icon={<CartIcon size="large" color="support" />}
              component={Link}
              data-testid="cart-icon-button"
              onClick={() => {
                track('click_button', trackingData);
              }}
            />
          }
          buttonSize={DEFAULT_ICON_BUTTON_HEIGHT}
          svgSize={DEFAULT_ICON_BUTTON_SVG_HEIGHT}
        />
      </span>
    </NotificationBadge>
  );
};

export default ShoppingCartGridItem;
